import { useCallback, useEffect, useMemo } from 'react';

import {
  useCreateConsumerFamily,
  useShowConsumerFamily,
  useUpdateConsumerFamily,
} from '@givebacks/api/backer';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { Button, Modal, Stack, TextInput, notifications } from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';

import { getErrorMessage_v2 } from '../../../../../utils/errorMessage';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../DirectorySettingsModalProvider';

type FamilyForm = {
  name: string;
};

const AddEditFamilyModalFooter = () => {
  const { openBack, openModalById, meta, setMeta } = useDirectorySettingsModalProvider({});

  const { useGetForm } = useFormyValidator<{
    family: UseFormReturnType<FamilyForm>;
  }>();

  const { mutate: createConsumerFamily } = useCreateConsumerFamily();
  const { mutate: updateConsumerFamily } = useUpdateConsumerFamily();

  const form = useGetForm('family');
  const handleAddFamily = useCallback(() => {
    if (!form?.validate().hasErrors) {
      createConsumerFamily(
        { name: form?.values?.name },
        {
          onSuccess: (data) => {
            notifications.show({
              message: `${form?.values?.name} family added`,
              variant: 'success',
            });
            setMeta({ familyId: data.family.uuid });
            openModalById('members');
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.values?.name, createConsumerFamily, setMeta, openModalById]);

  const handleSave = useCallback(() => {
    if (!form?.validate()?.hasErrors) {
      updateConsumerFamily(
        { familyId: meta.familyId ?? '', name: form?.values?.name },
        {
          onSuccess: () => {
            notifications.show({
              message: `${form?.values?.name} family saved`,
              variant: 'success',
            });
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.values?.name, updateConsumerFamily, meta.familyId, openBack]);

  const handleDelete = () => {
    openModalById('delete');
  };
  return (
    <Modal.AffixedFooter>
      {meta.familyId ? (
        <Stack gap="sm">
          <Button fullWidth onClick={handleSave}>
            Save Changes
          </Button>
          <Button color="cerise" variant="outline" fullWidth onClick={handleDelete}>
            Delete Family
          </Button>
        </Stack>
      ) : (
        <Button fullWidth onClick={handleAddFamily}>
          Add Family
        </Button>
      )}
    </Modal.AffixedFooter>
  );
};

const AddEditFamilyForm = () => {
  const { meta } = useDirectorySettingsModalProvider({});

  const { data: family } = useShowConsumerFamily({
    familyId: meta.familyId,
  });

  const form = useForm<FamilyForm>(
    {
      initialValues: {
        name: family?.name ?? '',
      },
      validate: {
        name: isNotEmpty('Family name is required'),
      },
    },
    {
      id: 'family',
    }
  );

  useEffect(() => {
    if (family) {
      form.initialize({ name: family.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family]);

  return (
    <TextInput
      label="Family Name"
      placeholder="Family Name"
      required
      {...form.getInputProps('name')}
    />
  );
};

const AddEditFamilyModal = () => {
  const { opened, close, meta } = useDirectorySettingsModalProvider({
    id: 'family',
  });

  const familyId = useMemo(() => meta?.familyId, [meta?.familyId]);

  const title = useMemo(() => {
    return familyId ? 'Edit Family' : 'Add Family';
  }, [familyId]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title}</ModalTitleWithBackButton>}
    >
      <AddEditFamilyForm />
      <AddEditFamilyModalFooter />
    </Modal>
  );
};

export { AddEditFamilyModal };
